import { useMemo, useState } from "react";
import { Card } from "antd";
import { toast } from "react-hot-toast";
import Footer from "../components/Footer";
import Header from "../components/Header";
import { useTranslation } from "react-i18next";
import StandardButton from "../components/StandardButton";

const DEFAULT_LINEAR_STOPS = [
  { color: "#6366f1", alpha: 1, position: 0 },
  { color: "#ec4899", alpha: 1, position: 100 },
];

const DEFAULT_RADIAL_STOPS = [
  { color: "#f59e0b", alpha: 1, position: 0 },
  { color: "#ef4444", alpha: 1, position: 100 },
];

const LINEAR_PRESETS = [
  {
    key: "sunset",
    angle: 135,
    stops: [
      { color: "#f97316", alpha: 1, position: 0 },
      { color: "#db2777", alpha: 1, position: 100 },
    ],
  },
  {
    key: "ocean",
    angle: 180,
    stops: [
      { color: "#0ea5e9", alpha: 1, position: 0 },
      { color: "#1e40af", alpha: 1, position: 100 },
    ],
  },
  {
    key: "lime",
    angle: 90,
    stops: [
      { color: "#bef264", alpha: 1, position: 0 },
      { color: "#14b8a6", alpha: 1, position: 100 },
    ],
  },
  {
    key: "candy",
    angle: 45,
    stops: [
      { color: "#fbcfe8", alpha: 1, position: 0 },
      { color: "#a78bfa", alpha: 1, position: 50 },
      { color: "#60a5fa", alpha: 1, position: 100 },
    ],
  },
  {
    key: "ember",
    angle: 0,
    stops: [
      { color: "#fde68a", alpha: 1, position: 0 },
      { color: "#f97316", alpha: 1, position: 50 },
      { color: "#7c2d12", alpha: 1, position: 100 },
    ],
  },
  {
    key: "mono",
    angle: 180,
    stops: [
      { color: "#e2e8f0", alpha: 1, position: 0 },
      { color: "#475569", alpha: 1, position: 100 },
    ],
  },
];

const RADIAL_PRESETS = [
  {
    key: "spotlight",
    shape: "circle",
    position: "center",
    stops: [
      { color: "#fef3c7", alpha: 1, position: 0 },
      { color: "#0f172a", alpha: 1, position: 100 },
    ],
  },
  {
    key: "bubble",
    shape: "circle",
    position: "top left",
    stops: [
      { color: "#ffffff", alpha: 0.8, position: 0 },
      { color: "#38bdf8", alpha: 1, position: 100 },
    ],
  },
  {
    key: "halo",
    shape: "ellipse",
    position: "center",
    stops: [
      { color: "#a855f7", alpha: 1, position: 0 },
      { color: "#1e1b4b", alpha: 1, position: 100 },
    ],
  },
];

const RADIAL_POSITIONS = [
  "center",
  "top",
  "right",
  "bottom",
  "left",
  "top left",
  "top right",
  "bottom left",
  "bottom right",
];

const BG_OPTIONS = [
  { key: "light", value: "#f8fafc" },
  { key: "dark", value: "#0f172a" },
  { key: "checker", value: "checker" },
];

const CHECKER_BG =
  "repeating-conic-gradient(#e2e8f0 0% 25%, #f8fafc 0% 50%) 50% / 24px 24px";

const clampInt = (raw, min, max) => {
  const n = Number.parseInt(raw, 10);
  if (Number.isNaN(n)) return min;
  return Math.max(min, Math.min(max, n));
};

const clampAlpha = (raw) => {
  const n = Number.parseFloat(raw);
  if (Number.isNaN(n)) return 0;
  return Math.max(0, Math.min(1, n));
};

const sanitizeHex = (raw) => {
  if (!raw) return "#000000";
  const v = raw.trim();
  if (/^#([0-9a-fA-F]{6})$/.test(v)) return v.toLowerCase();
  if (/^#([0-9a-fA-F]{3})$/.test(v)) {
    const r = v[1], g = v[2], b = v[3];
    return `#${r}${r}${g}${g}${b}${b}`.toLowerCase();
  }
  return null;
};

const hexToRgba = (hex, alpha) => {
  const clean = sanitizeHex(hex) || "#000000";
  const r = Number.parseInt(clean.slice(1, 3), 16);
  const g = Number.parseInt(clean.slice(3, 5), 16);
  const b = Number.parseInt(clean.slice(5, 7), 16);
  const a = Math.round(alpha * 100) / 100;
  if (a >= 1) return clean;
  return `rgba(${r}, ${g}, ${b}, ${a})`;
};

const stopToCss = (stop) => `${hexToRgba(stop.color, stop.alpha)} ${stop.position}%`;

const buildGradient = ({ type, angle, shape, position, repeating, stops }) => {
  const sorted = [...stops].sort((a, b) => a.position - b.position);
  const stopsCss = sorted.map(stopToCss).join(", ");
  if (type === "linear") {
    const fn = repeating ? "repeating-linear-gradient" : "linear-gradient";
    return `${fn}(${angle}deg, ${stopsCss})`;
  }
  const fn = repeating ? "repeating-radial-gradient" : "radial-gradient";
  return `${fn}(${shape} at ${position}, ${stopsCss})`;
};

const CssGradientGenerator = () => {
  const { t } = useTranslation();
  const [type, setType] = useState("linear");
  const [angle, setAngle] = useState(135);
  const [shape, setShape] = useState("circle");
  const [position, setPosition] = useState("center");
  const [repeating, setRepeating] = useState(false);
  const [linearStops, setLinearStops] = useState(
    DEFAULT_LINEAR_STOPS.map((s) => ({ ...s })),
  );
  const [radialStops, setRadialStops] = useState(
    DEFAULT_RADIAL_STOPS.map((s) => ({ ...s })),
  );
  const [bg, setBg] = useState("light");
  const [radius, setRadius] = useState(16);

  const stops = type === "linear" ? linearStops : radialStops;
  const setStops = type === "linear" ? setLinearStops : setRadialStops;

  const gradientCss = useMemo(
    () => buildGradient({ type, angle, shape, position, repeating, stops }),
    [type, angle, shape, position, repeating, stops],
  );

  const fullCss = `background: ${gradientCss};`;

  const updateStop = (idx, patch) => {
    setStops((prev) => prev.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
  };

  const addStop = () => {
    if (stops.length >= 8) {
      toast.error(t("gradient_max_stops", { max: 8 }));
      return;
    }
    const last = stops[stops.length - 1];
    const newPos = Math.min(100, Math.max(0, last.position + 10));
    setStops((prev) => [
      ...prev,
      { color: "#22d3ee", alpha: 1, position: newPos },
    ]);
  };

  const removeStop = (idx) => {
    if (stops.length <= 2) return;
    setStops((prev) => prev.filter((_, i) => i !== idx));
  };

  const applyLinearPreset = (preset) => {
    setType("linear");
    setAngle(preset.angle);
    setLinearStops(preset.stops.map((s) => ({ ...s })));
  };

  const applyRadialPreset = (preset) => {
    setType("radial");
    setShape(preset.shape);
    setPosition(preset.position);
    setRadialStops(preset.stops.map((s) => ({ ...s })));
  };

  const reset = () => {
    setType("linear");
    setAngle(135);
    setShape("circle");
    setPosition("center");
    setRepeating(false);
    setLinearStops(DEFAULT_LINEAR_STOPS.map((s) => ({ ...s })));
    setRadialStops(DEFAULT_RADIAL_STOPS.map((s) => ({ ...s })));
    setBg("light");
    setRadius(16);
  };

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(t("copy_success"));
    } catch {
      toast.error(t("failed_to_copy"));
    }
  };

  const bgStyle = (() => {
    const opt = BG_OPTIONS.find((o) => o.key === bg) || BG_OPTIONS[0];
    if (opt.value === "checker") return { background: CHECKER_BG };
    return { background: opt.value };
  })();

  const previewStyle = {
    background: gradientCss,
    borderRadius: `${radius}px`,
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-indigo-50 to-white p-0 text-gray-800">
      <Header />
      <div className="max-w-6xl mx-auto p-4">
        <Card className="shadow-lg rounded-xl">
          <div className="p-4 flex flex-col gap-5">
            <div>
              <h1 className="text-2xl font-bold text-indigo-700">
                {t("gradient_title")}
              </h1>
              <p className="text-gray-600 mt-1">{t("gradient_intro")}</p>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-medium text-gray-700">
                {t("gradient_type_label")}
              </span>
              {["linear", "radial"].map((k) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => setType(k)}
                  aria-pressed={type === k}
                  className={`px-3 py-1 text-xs rounded border ${
                    type === k
                      ? "border-indigo-500 text-indigo-700 bg-indigo-50"
                      : "border-gray-300 text-gray-600 hover:bg-gray-50"
                  }`}
                >
                  {t(`gradient_type_${k}`)}
                </button>
              ))}
              <label className="ml-2 flex items-center gap-1 text-xs text-gray-600 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={repeating}
                  onChange={(e) => setRepeating(e.target.checked)}
                  aria-label={t("gradient_repeating")}
                />
                {t("gradient_repeating")}
              </label>
              <StandardButton
                onClick={reset}
                type="secondary"
                variant="outlined"
                size="small"
              >
                {t("gradient_reset")}
              </StandardButton>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-medium text-gray-700">
                {t("gradient_presets_label")}
              </span>
              {type === "linear"
                ? LINEAR_PRESETS.map((p) => (
                    <StandardButton
                      key={p.key}
                      onClick={() => applyLinearPreset(p)}
                      type="secondary"
                      variant="outlined"
                      size="small"
                    >
                      {t(`gradient_preset_linear_${p.key}`)}
                    </StandardButton>
                  ))
                : RADIAL_PRESETS.map((p) => (
                    <StandardButton
                      key={p.key}
                      onClick={() => applyRadialPreset(p)}
                      type="secondary"
                      variant="outlined"
                      size="small"
                    >
                      {t(`gradient_preset_radial_${p.key}`)}
                    </StandardButton>
                  ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              <div className="flex flex-col gap-4">
                {type === "linear" ? (
                  <div className="border border-gray-200 rounded-lg p-3 bg-white/60">
                    <NumberSlider
                      label={t("gradient_angle")}
                      value={angle}
                      min={0}
                      max={360}
                      onChange={setAngle}
                      unit="deg"
                    />
                  </div>
                ) : (
                  <div className="border border-gray-200 rounded-lg p-3 bg-white/60 grid grid-cols-2 gap-3">
                    <div className="flex flex-col gap-1">
                      <label
                        htmlFor="gradient-shape"
                        className="text-xs font-medium text-gray-600"
                      >
                        {t("gradient_shape")}
                      </label>
                      <select
                        id="gradient-shape"
                        value={shape}
                        onChange={(e) => setShape(e.target.value)}
                        className="px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-indigo-400 focus:outline-none"
                      >
                        <option value="circle">{t("gradient_shape_circle")}</option>
                        <option value="ellipse">{t("gradient_shape_ellipse")}</option>
                      </select>
                    </div>
                    <div className="flex flex-col gap-1">
                      <label
                        htmlFor="gradient-position"
                        className="text-xs font-medium text-gray-600"
                      >
                        {t("gradient_position")}
                      </label>
                      <select
                        id="gradient-position"
                        value={position}
                        onChange={(e) => setPosition(e.target.value)}
                        className="px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-indigo-400 focus:outline-none"
                      >
                        {RADIAL_POSITIONS.map((p) => (
                          <option key={p} value={p}>
                            {p}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}

                {stops.map((stop, idx) => (
                  <div
                    key={idx}
                    className="border border-gray-200 rounded-lg p-3 bg-white/60"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-sm font-semibold text-gray-700">
                        {t("gradient_stop_n", { n: idx + 1 })}
                      </h3>
                      {stops.length > 2 && (
                        <button
                          type="button"
                          onClick={() => removeStop(idx)}
                          className="text-xs text-rose-600 hover:underline"
                          aria-label={t("gradient_remove_stop", { n: idx + 1 })}
                        >
                          {t("gradient_remove")}
                        </button>
                      )}
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div className="flex flex-col gap-1">
                        <label
                          htmlFor={`grad-color-${idx}`}
                          className="text-xs font-medium text-gray-600"
                        >
                          {t("gradient_color")}
                        </label>
                        <div className="flex items-center gap-2">
                          <input
                            id={`grad-color-${idx}`}
                            type="color"
                            value={sanitizeHex(stop.color) || "#000000"}
                            onChange={(e) =>
                              updateStop(idx, { color: e.target.value })
                            }
                            className="h-9 w-12 border border-gray-300 rounded cursor-pointer"
                            aria-label={t("gradient_color")}
                          />
                          <input
                            type="text"
                            value={stop.color}
                            onChange={(e) =>
                              updateStop(idx, { color: e.target.value })
                            }
                            onBlur={(e) => {
                              const safe = sanitizeHex(e.target.value);
                              if (safe) updateStop(idx, { color: safe });
                            }}
                            className="flex-1 min-w-0 px-2 py-1 text-sm font-mono border border-gray-300 rounded focus:ring-2 focus:ring-indigo-400 focus:outline-none"
                            aria-label={t("gradient_color_hex")}
                          />
                        </div>
                      </div>
                      <NumberSlider
                        label={t("gradient_opacity")}
                        value={stop.alpha}
                        min={0}
                        max={1}
                        step={0.01}
                        onChange={(v) => updateStop(idx, { alpha: v })}
                        isFloat
                      />
                    </div>

                    <div className="mt-3">
                      <NumberSlider
                        label={t("gradient_position_pct")}
                        value={stop.position}
                        min={0}
                        max={100}
                        onChange={(v) => updateStop(idx, { position: v })}
                        unit="%"
                      />
                    </div>
                  </div>
                ))}

                <StandardButton
                  onClick={addStop}
                  type="secondary"
                  variant="outlined"
                  size="small"
                  disabled={stops.length >= 8}
                >
                  {t("gradient_add_stop")}
                </StandardButton>
              </div>

              <div className="flex flex-col gap-3">
                <div className="flex flex-wrap items-center gap-3">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-gray-600">
                      {t("gradient_bg_label")}
                    </span>
                    {BG_OPTIONS.map((opt) => (
                      <button
                        key={opt.key}
                        type="button"
                        onClick={() => setBg(opt.key)}
                        aria-pressed={bg === opt.key}
                        className={`px-2 py-1 text-xs rounded border ${
                          bg === opt.key
                            ? "border-indigo-500 text-indigo-700 bg-indigo-50"
                            : "border-gray-300 text-gray-600 hover:bg-gray-50"
                        }`}
                      >
                        {t(`gradient_bg_${opt.key}`)}
                      </button>
                    ))}
                  </div>
                  <label className="flex items-center gap-2 text-xs text-gray-600">
                    {t("gradient_radius")}
                    <input
                      type="number"
                      min={0}
                      max={200}
                      value={radius}
                      onChange={(e) =>
                        setRadius(clampInt(e.target.value, 0, 200))
                      }
                      className="w-16 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-indigo-400 focus:outline-none"
                      aria-label={t("gradient_radius")}
                    />
                    px
                  </label>
                </div>

                <div
                  className="rounded-lg border border-gray-200 flex items-center justify-center overflow-hidden"
                  style={{
                    minHeight: 260,
                    padding: 32,
                    ...bgStyle,
                  }}
                  aria-label={t("gradient_preview_label")}
                >
                  <div
                    style={{
                      width: "100%",
                      height: 220,
                      ...previewStyle,
                    }}
                  />
                </div>

                <div className="flex items-center justify-between mt-2">
                  <h2 className="text-sm font-semibold text-gray-700">
                    {t("gradient_css_label")}
                  </h2>
                  <StandardButton
                    onClick={() => copy(fullCss)}
                    type="primary"
                    size="small"
                  >
                    {t("gradient_copy_css")}
                  </StandardButton>
                </div>
                <pre
                  className="text-xs font-mono bg-gray-900 text-emerald-200 p-3 rounded-md overflow-x-auto whitespace-pre"
                  aria-label={t("gradient_css_label")}
                >
{`.element {
    background: ${gradientCss};
}`}
                </pre>
              </div>
            </div>

            <div className="text-xs text-gray-500 leading-relaxed">
              <p>{t("gradient_note_stops")}</p>
              <p>{t("gradient_note_repeating")}</p>
              <p>{t("gradient_note_privacy")}</p>
            </div>
          </div>
        </Card>
      </div>
      <Footer />
    </div>
  );
};

const NumberSlider = ({
  label,
  value,
  min,
  max,
  step = 1,
  onChange,
  unit,
  isFloat = false,
}) => {
  const handle = (raw) => {
    if (isFloat) onChange(clampAlpha(raw));
    else onChange(clampInt(raw, min, max));
  };
  return (
    <div className="flex flex-col gap-1">
      <label className="text-xs font-medium text-gray-600">{label}</label>
      <div className="flex items-center gap-2">
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={(e) => handle(e.target.value)}
          className="flex-1 accent-indigo-600"
          aria-label={label}
        />
        <input
          type="number"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={(e) => handle(e.target.value)}
          className="w-16 px-1 py-0.5 text-sm text-right font-mono border border-gray-300 rounded focus:ring-2 focus:ring-indigo-400 focus:outline-none"
          aria-label={label}
        />
        {unit && <span className="text-xs text-gray-500 w-6">{unit}</span>}
      </div>
    </div>
  );
};

export default CssGradientGenerator;
